/**
 * Google Sheets Generation Report — Content Settings card.
 *
 * PR 2026-06-02. Shows:
 *   - Enable/disable toggle (PUT /api/admin/sites/:id/sheets-report)
 *   - Read-only auto-derived viewer email list (GET same path)
 *   - Read-only sheet URL link + sheet name (when sheet exists)
 *   - Last synced timestamp / last error
 *
 * Accessible from the Content Settings page so Content Managers can flip
 * the toggle for sites they manage; super_admins see all sites.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, ExternalLink, FileSpreadsheet, Users } from 'lucide-react';
import { authFetch } from '@/utils/auth-fetch';

interface SheetsReportPayload {
  siteId: string;
  siteName: string;
  enabled: boolean;
  spreadsheetUrl: string | null;
  spreadsheetName: string | null;
  viewerEmails: string[] | null;
  lastSyncedAt: string | null;
  lastCheckedAt: string | null;
  lastError: string | null;
  // codex iter-2 MINOR 2: explicit degraded signal so the UI can warn
  // when a backend lookup failed rather than silently showing "not
  // created yet" or an empty viewer list.
  degraded?: boolean;
  reportLookupError?: string | null;
  viewerLookupError?: string | null;
}

interface Props {
  siteId: string;
}

function fmtTimeAgo(iso: string | null): string {
  if (!iso) return 'never';
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.floor(ms / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export default function GoogleSheetsReportCard({ siteId }: Props) {
  const [data, setData] = useState<SheetsReportPayload | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toggling, setToggling] = useState(false);

  const fetchStatus = useCallback(async () => {
    if (!siteId) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await authFetch(`/api/admin/sites/${siteId}/sheets-report`);
      if (!resp.ok) {
        const body = await resp.text();
        throw new Error(`HTTP ${resp.status}: ${body.substring(0, 200)}`);
      }
      setData(await resp.json());
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const onToggle = async (next: boolean) => {
    if (!siteId || toggling) return;
    setToggling(true);
    setError(null);
    try {
      const resp = await authFetch(`/api/admin/sites/${siteId}/sheets-report`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: next }),
      });
      if (!resp.ok) {
        const body = await resp.text();
        throw new Error(`HTTP ${resp.status}: ${body.substring(0, 200)}`);
      }
      // Refresh full payload so viewer-list / sheet info reflect the new state.
      await fetchStatus();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setToggling(false);
    }
  };

  if (!siteId) return null;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5 text-green-700" />
            <CardTitle className="text-lg">Google Sheets Generation Report</CardTitle>
          </div>
          <div className="flex items-center gap-2">
            <Label htmlFor="sheets-toggle" className="text-sm">
              {data?.enabled ? 'Enabled' : 'Disabled'}
            </Label>
            <Switch
              id="sheets-toggle"
              checked={!!data?.enabled}
              disabled={loading || toggling}
              onCheckedChange={onToggle}
            />
          </div>
        </div>
        <CardDescription>
          When enabled, the hourly scheduler maintains a shared Google Sheet for this site with
          per-product generation status (~170K rows × 11 cols). Viewer access is auto-derived
          from site owner, assigned content managers, and PageMind staff — no manual entry.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {data?.lastError && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <strong>Last sync error:</strong> {data.lastError}
            </AlertDescription>
          </Alert>
        )}

        {/* codex iter-2 MINOR 2: degraded banner. Shown when the server
            couldn't read the report row OR couldn't derive the viewer
            list — distinguishes a real failure from "not created yet". */}
        {data?.degraded && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <strong>⚠ Some data couldn't be loaded.</strong>{' '}
              {data.reportLookupError && <>Report status: {data.reportLookupError}. </>}
              {data.viewerLookupError && <>Viewer list: {data.viewerLookupError}.</>}
            </AlertDescription>
          </Alert>
        )}

        {/* Spreadsheet info */}
        <div className="rounded border bg-muted/30 p-3">
          <div className="text-xs font-semibold uppercase text-muted-foreground mb-2">
            Spreadsheet
          </div>
          {data?.spreadsheetUrl ? (
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <a
                  href={data.spreadsheetUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-medium text-primary inline-flex items-center gap-1 hover:underline break-all"
                >
                  {data.spreadsheetName || 'Open spreadsheet'}
                  <ExternalLink className="h-3 w-3 flex-none" />
                </a>
              </div>
              <div className="text-xs text-muted-foreground">
                Last synced {fmtTimeAgo(data.lastSyncedAt)}
                {data.lastCheckedAt && ` · checked ${fmtTimeAgo(data.lastCheckedAt)}`}
              </div>
            </div>
          ) : (
            <div className="text-sm text-muted-foreground">
              {data?.enabled
                ? 'Not created yet — the scheduler will create it on its next hourly run (:05).'
                : 'No spreadsheet — enable the toggle above to create one.'}
            </div>
          )}
        </div>

        {/* Viewer list */}
        <div className="rounded border bg-muted/30 p-3">
          <div className="flex items-center gap-2 mb-2">
            <Users className="h-4 w-4 text-muted-foreground" />
            <div className="text-xs font-semibold uppercase text-muted-foreground">
              Viewers (auto-derived)
            </div>
            {data?.viewerEmails && (
              <Badge variant="secondary" className="text-xs">
                {data.viewerEmails.length}
              </Badge>
            )}
          </div>
          {data?.viewerEmails === null ? (
            <div className="text-sm text-amber-700">
              Viewer derivation failed — check server logs.
            </div>
          ) : data?.viewerEmails && data.viewerEmails.length > 0 ? (
            <ul className="text-sm space-y-1 max-h-40 overflow-y-auto">
              {data.viewerEmails.map((email) => (
                <li key={email} className="font-mono text-xs">
                  {email}
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-sm text-muted-foreground">
              No viewers will be granted access — confirm site owner + assigned content managers are set up.
            </div>
          )}
          <div className="text-xs text-muted-foreground mt-2">
            Derived from: site owner + assigned content managers (this site) + all PageMind super admins.
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
